import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import usuarioService from '../services/UsuarioService';
import ColegioService from '../services/ColegioService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import UserSessionMenu from './common/UserSessionMenu';
import AdminMenu from './common/menus/AdminMenu';
import { useAuth } from '../contexts/AuthContext';
import { ADMIN_FORM_MENU_ITEMS, navigateAdminMenuItem } from '../utils/adminMenu';

const AdminCreateProfesor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const prefillEmail = location.state?.email || '';
  const stateColegioId = location.state?.idColegio || null;
  const isEmailLocked = Boolean(prefillEmail);

  const activeColegio = useMemo(() => ColegioService.getActiveColegio(), []);
  const activeColegioId = useMemo(() => stateColegioId || ColegioService.getActiveColegioId(), [stateColegioId]);
  const activeColegioName = activeColegio?.nombre || activeColegio?.name || activeColegio?.descripcion || 'Colegio';
  const hasModuloAlumnos = useMemo(() => {
    const flags = ColegioService.getFeatureFlags();
    return Array.isArray(flags) && flags.some((flag) => {
      const name = flag?.nombre || flag?.Nombre || '';
      const activo = flag?.activo ?? flag?.Activo ?? false;
      return name.toLowerCase() === 'moduloalumnos' && !!activo;
    });
  }, []);

  const [form, setForm] = useState({
    email: prefillEmail,
    nombre: '',
    apellido: '',
    telefono: '',
    dni: '',
    permiteCrearAula: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // success | error | info

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activeColegioId) {
      setMessage('Selecciona un colegio antes de crear el profesor.');
      setMessageType('error');
      return;
    }
    const emailValue = (form.email || '').trim().toLowerCase();
    if (!emailValue) {
      setMessage('Completa email para continuar.');
      setMessageType('error');
      return;
    }
    const dniValue = (form.dni || '').trim();
    if (!dniValue) {
      setMessage('Completa DNI para continuar.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('Creando profesor...');
    setMessageType('info');
    try {
      const displayName = `${form.nombre || ''} ${form.apellido || ''}`.trim() || emailValue;
      const payload = {
        ...form,
        email: emailValue,
        displayName,
        dni: dniValue,
        idColegio: activeColegioId
      };
      if (!hasModuloAlumnos) {
        delete payload.permiteCrearAula;
      }
      await usuarioService.createProfesor(payload);
      setMessage('Profesor creado correctamente.');
      setMessageType('success');
      setTimeout(() => navigate('/admin-dashboard', { replace: true, state: { tab: 'profesores' } }), 1000);
    } catch (error) {
      setMessage(error.message || 'No pudimos crear el profesor.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="new-dashboard admin-mode">
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-university"></i>
            <span>Admin</span>
          </div>
        </div>
        <AdminMenu
          activeId="profesores"
          user={user}
          featureFlags={ColegioService.getFeatureFlags()}
          baseItems={ADMIN_FORM_MENU_ITEMS}
          onNavigate={(item) => navigateAdminMenuItem(navigate, item)}
        />
        <div className="sidebar-footer">
          <ActiveColegioBadge />
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
              <span className="user-role">Admin</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">Alta de profesor</h1>
            <p className="page-subtitle">Crearás un nuevo profesor para el colegio {activeColegioName}.</p>
          </div>
          <div className="header-right">
            <ActiveColegioBadge />
            <UserSessionMenu user={user} onLogout={logout} showStandaloneLogout={false} userFallbackLabel="Administrador" />
          </div>
        </header>

        <div className="dashboard-content" style={{ padding: '2rem', maxWidth: '900px' }}>
          <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-user-plus" />
                <span>Datos del profesor</span>
              </div>
            </div>
            <div className="card-content">
              <p style={{ marginTop: 0, color: '#475569' }}>
                Completa los datos requeridos para registrar al nuevo docente en el colegio {activeColegioName}. Su DNI se usará como contraseña inicial; luego deberá actualizarla.
              </p>
              <form onSubmit={handleSubmit} className="prof-create-form">
                <div className="form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.email}
                    placeholder="profesor@ejemplo.com"
                    readOnly={isEmailLocked}
                    onChange={(e) => !isEmailLocked && handleChange('email', e.target.value)}
                  />
                </div>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Nombre</label>
                    <input
                      type="text"
                      value={form.nombre}
                      onChange={(e) => handleChange('nombre', e.target.value)}
                      placeholder="Juan"
                    />
                  </div>
                  <div className="form-field">
                    <label>Apellido</label>
                    <input
                      type="text"
                      value={form.apellido}
                      onChange={(e) => handleChange('apellido', e.target.value)}
                      placeholder="Perez"
                    />
                  </div>
                </div>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Teléfono</label>
                    <input
                      type="text"
                      value={form.telefono}
                      onChange={(e) => handleChange('telefono', e.target.value)}
                      placeholder="123456"
                    />
                  </div>
                  <div className="form-field">
                  <label>DNI</label>
                  <input
                    type="text"
                    value={form.dni}
                    onChange={(e) => handleChange('dni', e.target.value)}
                    placeholder="30123456"
                  />
                  </div>
                </div>

                {hasModuloAlumnos && (
                  <div className="form-field" style={{ padding: '0.75rem 0', borderTop: '1px solid var(--gray-200)', marginTop: '0.25rem' }}>
                    <label htmlFor="permiteCrearAula" style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600 }}>
                      Permisos adicionales
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <input
                        type="checkbox"
                        id="permiteCrearAula"
                        checked={!!form.permiteCrearAula}
                        onChange={(e) => handleChange('permiteCrearAula', e.target.checked)}
                        style={{ width: '18px', height: '18px' }}
                      />
                      <label htmlFor="permiteCrearAula" style={{ margin: 0 }}>
                        Permite crear aulas
                      </label>
                      <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                        Habilita al docente a crear aulas del módulo alumnos.
                      </span>
                    </div>
                  </div>
                )}

                {message && (
                  <div
                    className="modal-message"
                    style={{
                      marginTop: '0.5rem',
                      borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                      background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                      color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                    }}
                  >
                    {isSubmitting && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                    {message}
                  </div>
                )}

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin-dashboard', { state: { tab: 'profesores' } })}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                    <span>Crear profesor</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminCreateProfesor;
